import { OrganizationDTOV2 } from '@workers-benefit-fund/api'
import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { ALL_ORGANIZATION_NAMES } from 'src/v2/commons/config'
import { ALL_ROUTES } from 'src/v2/domains/routing/constants/route-configs.constants'

export interface OrganizationCheckResult {
  needsSwitch: boolean
  targetOrganization?: OrganizationDTOV2
  hasAccess: boolean
  urlOrganizationName?: string
  isPublicRoute: boolean
  targetPath?: string
  isInvalidOrganization: boolean
}

export const extractOrganizationFromPath = (
  pathname: string,
): string | null => {
  if (!pathname || pathname === '/') return null

  const normalizedPath = pathname.startsWith('/')
    ? pathname.substring(1)
    : pathname
  const segments = normalizedPath.split('/').filter(Boolean)

  if (segments.length === 0) return null

  return segments[0]
}

export const isPublicRoute = (pathname: string): boolean => {
  const publicRoutes = Object.values(ALL_ROUTES).filter(
    (route) => !route.requiresAuth,
  )

  return publicRoutes.some((route) => {
    const routePath = route.path.startsWith('/') ? route.path : `/${route.path}`
    return pathname === routePath || pathname.startsWith(routePath)
  })
}

export const checkIfNeedsOrganizationSwitch = (
  targetPathname: string,
  currentOrganization: OrganizationDTOV2,
  userOrganizations: OrganizationDTOV2[],
): OrganizationCheckResult => {
  if (isPublicRoute(targetPathname)) {
    return {
      needsSwitch: false,
      hasAccess: true,
      isPublicRoute: true,
      isInvalidOrganization: false,
    }
  }

  const urlOrgName = extractOrganizationFromPath(targetPathname)

  if (!urlOrgName) {
    return {
      needsSwitch: false,
      hasAccess: true,
      isPublicRoute: false,
      isInvalidOrganization: false,
    }
  }

  const organizationExistsInSystem = ALL_ORGANIZATION_NAMES.includes(urlOrgName)

  if (!organizationExistsInSystem) {
    return {
      needsSwitch: false,
      targetOrganization: undefined,
      hasAccess: false,
      urlOrganizationName: urlOrgName,
      isPublicRoute: false,
      targetPath: targetPathname,
      isInvalidOrganization: true,
    }
  }

  const isCurrentOrganization =
    cleanStringToBeUrlReadable(currentOrganization.name) === urlOrgName

  if (isCurrentOrganization) {
    return {
      needsSwitch: false,
      targetOrganization: currentOrganization,
      hasAccess: true,
      urlOrganizationName: urlOrgName,
      isPublicRoute: false,
      targetPath: targetPathname,
      isInvalidOrganization: false,
    }
  }

  const targetOrg = userOrganizations.find(
    (org) => cleanStringToBeUrlReadable(org.name) === urlOrgName,
  )

  return {
    needsSwitch: !!targetOrg,
    targetOrganization: targetOrg,
    hasAccess: !!targetOrg,
    urlOrganizationName: urlOrgName,
    isPublicRoute: false,
    targetPath: targetPathname,
    isInvalidOrganization: false,
  }
}
